import { Box, Typography } from "@mui/material";
import { CommonStyle } from "Components";
import CstmBtn from "Components/CstmBtn";
import { FC, Fragment, useEffect, useState } from "react";
import { CheckNumber } from "Utils/common";
import { ModalLayout } from "Layouts";
import { CommonButtonArray } from "Components/Modal/Data";
import {
  PrblmActivePlan,
  PreApprovedOptionType,
  PreApprovedPlanType
} from "DataTypes/PatientManagement.type";
import PreApprovedPlanApi from "Service/PreApprovedPlan.api";
import PreApprovedPlanFormatter from "Utils/PreApprovedPlan.Formatter";
import ReusablePAPUI from "Components/ReusablePAPUI";
import NoData from "Components/NoData";
import { PatientInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import PatientFormatter from "Utils/Patient.Formatter";
import ImgPath from "Constant/Imgs";
import Stages from "Constant/Stages";

interface CPPAPUIProps {
  problemData: PrblmActivePlan;
  handleUsePAP: (data: PrblmActivePlan) => void;
  handleEditPAP: (data: PrblmActivePlan) => void;
  handleSave: () => void;
  readOnly: boolean;
}

const CPPreApprovedPlanDetails: FC<CPPAPUIProps> = (props) => {
  const { selectedPrgm }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );
  const careId = selectedPrgm.careplanId;
  const {
    problemData,
    handleUsePAP,
    handleEditPAP,
    handleSave,
    readOnly
  } = props;
  const [openEditWarnModal, setOpenEditWarnModal] = useState(false);
  const [planData, setPlanData] = useState<PreApprovedPlanType | null>(null);

  useEffect(() => {
    handleGetData();
  }, [problemData.id, problemData.isPlanInUse]);

  const handleGetData: () => void = async () => {
    let res;
    if (problemData.isPlanInUse) {
      const payload = {
        carePlanId: careId,
        problemId: problemData.problemId
      }
      res = await PreApprovedPlanApi.SearchPAPForCP({ data: payload });
    } else {
      res = await PreApprovedPlanApi.PlanSearch({
        id1: problemData.activePlanId
      });
    }
    if (res?.success) {
      const formattedData = PreApprovedPlanFormatter.PlanSearch(res?.data);
      setPlanData(formattedData);
    }
  };

  const handleAddUpdateOption: (
    data: PreApprovedOptionType,
    catType: string
  ) => Promise<boolean> = async (editData, catType) => {
    const payload = {
      ...editData,
      careplanId: careId,
      problemId: problemData.problemId,
      categoryId: planData?.category.find(
        catEl => catEl.categoryType == catType
      )?.categoryId,
      categoryType: catType
    }
    const res = await PreApprovedPlanApi.AddUpdateCPOption({ data: payload });
    if (res?.success) {
      const newOptId = CheckNumber(res?.data?.id);
      planData && setPlanData({
        ...planData,
        category: planData.category.map(catEl => {
          if (catEl.categoryType == catType && editData) {
            if (editData?.id) {
              catEl.options = catEl.options.map(optEl => {
                if (optEl.id == editData.id) optEl = editData
                return optEl;
              })
            } else catEl.options.push({ ...editData, id: newOptId })
          }
          return catEl;
        })
      })
      return true;
    } else return false
  };

  const handleDeleteOption: (
    deleteOptId: number | null,
    catType: string
  ) => void = async (deleteOptId, catType) => {
    const res = await PreApprovedPlanApi.DeleteCPOption({ id1: deleteOptId });
    if (res?.success) {
      planData && setPlanData({
        ...planData,
        category: planData.category.map(catEl => {
          if (catEl.categoryType == catType && deleteOptId) {
            catEl.options = catEl.options.filter(
              optEl => optEl.id != deleteOptId
            );
          }
          return catEl;
        })
      });
    }
  };

  return (
    <Box
      width="66%"
      height="100%"
      sx={{ ...CommonStyle.sxWhiteBox, overflow: "auto" }}
    >
      <ModalLayout
        modwidth="30%"
        heading={"Warning"}
        content={
          <Box textAlign={"center"}>
            <Typography>
              {"After editing this pre-approved plan," +
                "provider's sign off will be mandatory."}
            </Typography>
            <Typography my={1}>
              Are you sure, you want to continue?
            </Typography>
          </Box>
        }
        open={openEditWarnModal}
        onClose={() => setOpenEditWarnModal(false)}
        buttonArr={CommonButtonArray(
          () => {
            handleEditPAP(problemData);
            setOpenEditWarnModal(false);
          },
          () => setOpenEditWarnModal(false),
          "Edit Plan",
          "Cancel"
        )}
      />
      <Box p={2}>
        <Box width={"100%"}>
          {planData ?
            <Fragment>
              <Box
                sx={{
                  ...CommonStyle.sxRow,
                  justifyContent: "space-between",
                }}
              >
                <Typography mb={1} variant="body2">
                  <b>Plan For :</b> {planData?.problemName}
                </Typography>
                {readOnly ? (
                  (selectedPrgm?.careplanStage || 0) >
                  PatientFormatter.GetCPStage(Stages.CPQues) && (
                    <Box
                      onClick={handleSave}
                      sx={{
                        ...CommonStyle.sxRoundIconsMainSm,
                        mr: 1,
                        p: 0.1,
                        "&>svg": {
                          rotate: "180deg",
                        },
                      }}
                    >
                      <ImgPath.BreadcrumbIcon
                        titleAccess="Next"
                        sx={{ width: "0.9rem" }}
                      />
                    </Box>
                  )
                ) : (
                  <Box
                    onClick={handleSave}
                    sx={{
                      ...CommonStyle.sxRoundIconsMainSm,
                      mr: 1,
                      p: 0.1,
                      "&>svg": {
                        rotate: "180deg",
                      },
                    }}
                  >
                    <ImgPath.BreadcrumbIcon
                      titleAccess="Save & Next"
                      sx={{ width: "0.9rem" }}
                    />
                  </Box>
                )}
              </Box>
              <ReusablePAPUI
                handleAddUpdateOption={handleAddUpdateOption}
                handleDeleteOption={handleDeleteOption}
                isPlanEditable={problemData.isPlanEdited && !readOnly}
                planData={planData}
              />
              <Box
                display="flex"
                justifyContent="flex-end"
                sx={{ ">button": { mr: 1 } }}
              >
                {!problemData.isPlanEdited &&
                  !readOnly &&
                  problemData.isPlanInUse &&
                  <CstmBtn
                    label="Edit Plan"
                    onClick={() => setOpenEditWarnModal(true)}
                    sxProps={{ mt: 1, p: 2 }}
                  />
                }
                {!problemData.isPlanInUse &&
                  !readOnly &&
                  <CstmBtn
                    label="Use this plan"
                    onClick={() => handleUsePAP(problemData)}
                    sxProps={{ mt: 1, p: 2 }}
                  />
                }
              </Box>
            </Fragment>
            :
            <NoData />
          }
        </Box>
      </Box>
    </Box>
  )
};

export default CPPreApprovedPlanDetails;